import React, { FC, useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  FormInstance,
  notification,
  Upload,
  Image,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  CloseCircleOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import CommonService from "../../services/common.service";
import { BASE_IMAGE_URL, formItemLayout } from "../../services/constants";
import { PrivateUIRoutes } from "../../router";
import { INews } from "../../models/news.model";
import NewsService from "../../services/news.service";
import Editor from "../Layout/Common/Adapters/Editor";

interface IProps {
  data: INews;
  form: FormInstance;
}

const NewsForm: FC<IProps> = ({ data, form }) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [changedFile, setChangedFile] = useState(null);
  const [changedFileBase64, setChangedFileBase64] = useState<any>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setContent(data?.content);
  }, [data]);

  const update = async (values: INews) => {
    setLoading(true);

    try {
      await NewsService.update({ ...data, ...values, content });

      notification.success({ message: "News has been updated" });

      setLoading(false);

      navigate(`/${PrivateUIRoutes.CONTROL_PANEL}/${PrivateUIRoutes.NEWS}`);
    } catch (errors: any) {
      setLoading(false);

      CommonService.showAllErrors(errors);
    }
  };

  const beforeUpload = (file: File) => {
    if (file.size / 1024 / 1024 > 5) {
      notification.error({ message: "Image must smaller than 5MB!" });
    }

    return false;
  };

  const handleChangeFile = (info: any) => {
    setChangedFile(info.file);

    const reader = new FileReader();
    reader.addEventListener("load", () => setChangedFileBase64(reader.result));
    reader.readAsDataURL(info.file);
  };

  const handleSubmitForm = async (values: any) => {
    await update(values);
  };

  const uploadButton = (
    <div className="fs-22">
      {loading ? <LoadingOutlined /> : <UploadOutlined />}
    </div>
  );

  return (
    <Form
      {...formItemLayout}
      form={form}
      hideRequiredMark={false}
      onFinish={handleSubmitForm}
    >
      <Form.Item
        label="Title"
        name="title"
        rules={[{ required: true, message: "Please input title!" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Short description"
        name="shortDescription"
        rules={[{ required: true, message: "Please input short description!" }]}
      >
        <Input.TextArea rows={4} />
      </Form.Item>
      <Form.Item label="Content">
        <Editor content={content || ""} setContent={setContent} />
      </Form.Item>
      <Form.Item label="Image">
        {data.image && (
          <Image width={300} src={`${BASE_IMAGE_URL}/img/news/${data.image}`} />
        )}
        <div
          className="d-flex justify-content-start align-items-start"
          style={{ width: "110px" }}
        >
          <Upload
            accept="image/png,image/jpeg"
            name="avatar"
            listType="picture-card"
            showUploadList={false}
            beforeUpload={beforeUpload}
            onChange={handleChangeFile}
          >
            {changedFileBase64 ? (
              <div>
                <img
                  src={changedFileBase64}
                  alt="avatar"
                  style={{ maxHeight: 102, maxWidth: 102 }}
                />
              </div>
            ) : (
              uploadButton
            )}
          </Upload>
          {changedFile && (
            <CloseCircleOutlined
              className="upload-close"
              onClick={() => {
                setChangedFile(null);
                setChangedFileBase64(null);
              }}
            />
          )}
        </div>
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 5, span: 19 }}>
        <Button type="primary" htmlType="submit" loading={loading}>
          Save changes
        </Button>
      </Form.Item>
    </Form>
  );
};

export default NewsForm;
